import React from "react";
import {IconButton, Tooltip, Typography, Popper, Paper} from "@mui/material";
import {ChevronLeft, ChevronRight, SpaceDashboardOutlined, HowToRegOutlined, TimelineOutlined,
        FactCheckOutlined, RuleFolderOutlined, PublishedWithChangesOutlined,
        HelpOutlineOutlined, SchoolOutlined,
        DocumentScannerOutlined, DescriptionOutlined, CategoryOutlined,
        StorefrontOutlined, LocalShippingOutlined, WarehouseOutlined, StraightenOutlined,
        FolderSpecialOutlined, FolderOutlined} from "@mui/icons-material";
import {OBJECT_CLASSIFICATIONS, objectPrivilegeCode} from "../../util/objectClassifications";
import {useLocation, useNavigate} from "react-router";
import {hasPrivilege} from "../../util/SessionContext";
import {Blue3, Grey2, Grey5, Grey7, Grey17, White} from "../../theme/styles";
import css_buttons from "../Common/css/CCButton.module.scss";
// FROM THE REGISTRY, not hand-written here, so the flyout entry and its route can no more
// disagree than the top-level ones can -- and so a curriculum classification that is ever
// renamed or rerouted carries its label and path along with it.
const TRAINING_CURRICULA = OBJECT_CLASSIFICATIONS.filter(c => c.code === "TrainingCurriculum");
const PROJECTS = OBJECT_CLASSIFICATIONS.filter(c => c.code === "Project");

/** WHETHER THIS READER KEEPS RECORDS AT ALL, beyond projects and what is filed in them (Ed, 2026-11-04: "It will be
 * possible to use the QMS product various ways"). Change Orders, Approvals, Units of Measure and Training only mean
 * something where there are records to change, sign, measure or train on, so a tenant using QMS for projects alone --
 * Evolve, whose writing lives in standalone Parkour Docs in folders -- is not offered four doors onto nothing. Any
 * classification counts (Documents included) except Project and the kinds `filedInProject`. Rail tidying, as the
 * `<type>:view` gates below are: the pages themselves are unchanged. */
export function keepsRecords(canSee: (code: string) => boolean): boolean {
    return OBJECT_CLASSIFICATIONS.some(c => !c.filedInProject && c.code !== "Project" && canSee(c.code));
}

// ── HOW THE RAIL IS GROUPED ──────────────────────────────────────────────────────────────────
//
// TWENTY-TWO ENTRIES WAS SIXTEEN TOO MANY, and the sixteen were the problem: one 22px glyph each
// for Documents, Items, Inventory, Equipment, Customers, Suppliers, Distributors, Contracts,
// Purchase Orders, Forms, Prepared Quotes, Build Plans, Quality Records, Questionnaires, Products
// and Experiments. Telling *Items* from *Products* from *Experiments* from *Inventory* at that size
// is not a solvable icon problem -- there is no glyph for "item" that reads differently from
// "product" -- so the rail was unreadable until hovered or opened, which is how Ed described it.
//
// t4 IS THE MODEL AND IT IS NOT HIERARCHICAL. Its rail is five flat entries -- Compositions,
// Builds & products, Blast radius, then Settings and Documentation -- and what makes it legible is
// that there are five and each names a DOMAIN rather than an entity. That is the property copied
// here; the mechanism was already present, because Approvals and Training have been flyouts since
// this rail was written.
//
// THE REGISTRY REMAINS THE SOURCE OF LABEL, ROUTE AND ICON. These lists name CODES only, so a
// classification that is renamed or rerouted carries its own label and path into the flyout -- the
// same reason the flat list mapped the registry rather than repeating it. A code that appears in no
// group would silently vanish from the rail, so GROUPED_CODES is checked against the registry below
// and anything unclaimed is appended on its own rather than lost.
//
// MEMBERSHIP IS BY SUBJECT, which is the rule the two existing flyouts already follow ("by
// SUBJECT, not by whose it is"). Forms sit with records because a form is a document's shape;
// Quality Records sit with records because an issue is a record of something that happened, not a
// person's workload.
//
// AND FOUR OF THEM ARE NOT ENTITIES AT ALL -- removed 2026-10-26, on Ed's rule that "Products,
// Quote Request, Quote Responses, Contracts and Build Plans are ingredients to a project and will
// not have visibility controls or a lifecycle process... they are to only be available through the
// project detail page". A rail entry is a claim that something is a subject you go and look at a
// list of; these are contents of a folder, and Projects is the folder.
//
// THEY ARE EXCLUDED BY THE REGISTRY'S OWN `filedInProject` FLAG AND NOT BY BEING LEFT OUT OF THESE
// LISTS, which would have done the opposite of what it looks like: the unclaimed fallback below
// gives any code in no group its own TOP-LEVEL entry, on purpose, so that adding a classification
// cannot silently lose its page. Deleting these four from the groups alone would have promoted all
// four to the top of the rail.
//
// QUOTE REQUESTS JOINED THE REGISTRY ON 2026-10-27 as the nineteenth species, already flagged
// `filedInProject`, so they were never in these groups and never reach the rail -- the same way
// as the other four, not by being left out. Before that a QuoteRequest was "a plain row"
// (project.py) with no classification at all.
export const GROUPS: {key: string; label: string; icon: string; codes: string[]}[] = [
    {key: "records", label: "Documents & records", icon: "DescriptionOutlined",
     codes: ["Document", "FORM", "QualityRecord"]},
    // RENAMED WITH ITS MEMBERS. "Products & engineering" named a classification that has left
    // the rail, and a group heading that promises a page it cannot open is worse than a dull one.
    {key: "engineering", label: "Parts & development", icon: "CategoryOutlined",
     codes: ["Item", "Experiment"]},
    {key: "commerce", label: "Commerce", icon: "StorefrontOutlined",
     codes: ["Customer", "PurchaseOrder", "Distributor"]},
    {key: "supply", label: "Supply", icon: "LocalShippingOutlined",
     codes: ["Supplier", "Questionnaire"]},
    {key: "operations", label: "Operations", icon: "WarehouseOutlined",
     codes: ["Lot", "Receivable", "Inventory", "Equipment"]},
];

// Held back from the groups and placed by hand: a curriculum belongs with My Training, which is
// the pairing the Training flyout already makes.
export const UNGROUPED_CODES = ["TrainingCurriculum"];

// PLACED AT A CHOSEN POSITION, WHICH IS NOT THE SAME THING AS UNGROUPED. A code in no group falls
// to the unclaimed fallback, and the fallback appends -- so when Project joined the registry on
// 2026-10-27 and its hand-written entry was deleted, the rail stopped losing it (good) and started
// drawing it after Operations, next to the curriculum (not good). It had sat directly under
// Dashboard on purpose: it is the folder the commercial arc hangs in, so it belongs above the
// records rather than below them, and Ed noticed it had moved before any test did.
//
// LISTED HERE SO THE ORDER IS ONE FACT. `railClassificationCodes` puts these first and the
// component renders them first, and `unclaimedRailCodes` treats them as claimed so the fallback
// cannot draw a second copy further down.
export const PLACED_CODES = ["Project"];

/**
 * Which classifications the rail offers, in rail order, for a reader who may see these codes.
 *
 * EXTRACTED SO IT CAN BE ASSERTED, and because the thing worth asserting is the INTERACTION rather
 * than either list. Removing a code from GROUPS does not remove it from the rail -- the unclaimed
 * fallback below hands anything ungrouped its own top-level entry, deliberately, so that adding a
 * classification cannot silently lose its page. So "is Contracts in the rail?" cannot be answered
 * by reading GROUPS, which is exactly the mistake this function exists to make untellable.
 *
 * The component builds its categories from the same two lists and the same two filters; this is the
 * membership half of that, with the icons and the React left out.
 */
export function railClassificationCodes(canSee: (code: string) => boolean): string[] {
    return [...PLACED_CODES.filter(canSee),
            ...GROUPS.flatMap(group => group.codes.filter(canSee)),
            ...unclaimedRailCodes(canSee),
            ...UNGROUPED_CODES.filter(canSee)];
}

/**
 * The fallback's membership: every classification no group claims and the rail should still offer.
 *
 * ONE DEFINITION, CALLED BY BOTH -- the component builds its own top-level entries from this and
 * the guard reads it, so they cannot drift. A second copy of these two filters written for the test
 * would assert that the copy is right, which is the failure mode a guard is supposed to catch.
 */
export function unclaimedRailCodes(canSee: (code: string) => boolean): string[] {
    const claimed = new Set([...GROUPS.flatMap(g => g.codes), ...UNGROUPED_CODES, ...PLACED_CODES]);
    return OBJECT_CLASSIFICATIONS
        // A PROJECT'S INGREDIENTS ARE NOT UNCLAIMED, THEY ARE PLACED ELSEWHERE. Without this the
        // fallback would hand each of the four its own top-level entry -- the exact opposite of
        // removing them -- because the fallback cannot tell "nobody has grouped this yet" from
        // "this does not belong in the rail".
        .filter(c => !c.filedInProject)
        .filter(c => !claimed.has(c.code))
        .filter(c => canSee(c.code))
        .map(c => c.code);
}

const CLOSED_WIDTH = 64;
const OPEN_WIDTH = 280;
const ACTIVE_BG = "#E8F4FA";
const HOVER_BG = "#F7F9FC";
const CLOSE_DELAY_MS = 180;

// NO ICON. Nothing draws one: the flyout lists members as text under a heading that already
// names the domain, and the inset rows follow the data catalog's rail, whose NavItem is label and
// route alone. A field every construction site filled in and no renderer read is dead weight that
// reads as live.
type SubItem = {label: string; route: string};
type Category = {
    key: string;
    icon: React.ReactNode;
    label: string;
    route?: string;
    subitems: SubItem[];
    // About the app rather than a destination within it -- Documentation, and Settings when this
    // app grows one. The rail's single divider sits above the first of these.
    trailing?: boolean;
};

export default function AppsSidebar() {
    const navigate = useNavigate();
    const location = useLocation();

    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [openKey, setOpenKey] = React.useState<string | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const closeTimerRef = React.useRef<number | null>(null);

    const iconSx = {color: Blue3, width: "22px", height: "22px"};

    const GROUP_ICONS: Record<string, React.ElementType> = {
        DescriptionOutlined, CategoryOutlined, StorefrontOutlined, LocalShippingOutlined,
        WarehouseOutlined,
    };

    // One flyout per group, holding the classifications this reader may see, in the order the
    // group names them -- deliberately NOT alphabetical: a group of four reads as a sentence
    // (Items, Products, Experiments, Build Plans) and alphabetising it would scatter that.
    const visible = (code: string) => {
        const found = OBJECT_CLASSIFICATIONS.find(c => c.code === code);
        return found && hasPrivilege(objectPrivilegeCode(found, "view")) ? found : null;
    };

    const groupedCategories: Category[] = GROUPS.flatMap(group => {
        const members = group.codes.map(visible).filter(Boolean) as typeof OBJECT_CLASSIFICATIONS;
        if (members.length === 0) return [];
        const GroupIcon = GROUP_ICONS[group.icon];
        return [{
            key: group.key,
            icon: <GroupIcon sx={iconSx}/>,
            label: group.label,
            // LABEL AND ROUTE ALONE. SubItem carries no icon and nothing draws one, which the
            // type's own comment says at length -- so binding c.icon here was dead weight that
            // read as live, and the only thing that noticed was an eslint warning.
            subitems: members.map(c => ({label: c.plural, route: c.route})),
        }];
    });

    // A CODE IN THE REGISTRY AND IN NO GROUP WOULD VANISH FROM THE RAIL SILENTLY, which is the one
    // way this change could lose a page. So anything unclaimed gets its own top-level entry, as it
    // had before -- the rail degrades to the old behaviour for a new classification rather than
    // dropping it, and whoever adds one can put it in a group when they notice it sitting alone.
    const unclaimedCategories: Category[] = unclaimedRailCodes(
            code => hasPrivilege(objectPrivilegeCode(
                OBJECT_CLASSIFICATIONS.find(c => c.code === code)!, "view")))
        .map(code => {
            const c = OBJECT_CLASSIFICATIONS.find(one => one.code === code)!;
            const Icon = c.icon;
            return {key: c.code, icon: <Icon sx={iconSx}/>, label: c.plural, route: c.route,
                    subitems: []};
        });

    // Every entry here MUST point at a route AppRoutes actually serves. Unmatched paths fall through
    // to <Route path="/*"> and silently redirect to "/", so a wrong route reads as a button that does
    // nothing rather than as an error -- which is exactly how the previous rail's dead entries hid.
    // The types, expander and rendering below handle both routed items (route, empty subitems) and
    // flyout categories.
    const records = keepsRecords(code => hasPrivilege(objectPrivilegeCode(OBJECT_CLASSIFICATIONS.find(c => c.code === code)!, "view")));

    const categories: Category[] = [
        {key: "dashboard", icon: <SpaceDashboardOutlined sx={iconSx}/>, label: "Dashboard", route: "/", subitems: []},
        // THE CONTAINER THE COMMERCIAL ARC HANGS IN, and it sits above the records because it is
        // what they are filed in rather than one of them.
        //
        // PLACED HERE, DRIVEN BY THE REGISTRY. This block used to hard-code the label, route and
        // gate, on the argument that "a project is not an object either: no revision chain, no
        // approval, no signature". That was wrong -- Ed's rule of 2026-10-26 said the opposite --
        // and Project became the eighteenth classification on 2026-10-27, so the label, route and
        // icon come from its registry row and the gate is `project:view` like every other one.
        //
        // WHAT THAT CORRECTION BROKE, AND WHY THE ENTRY IS BACK IN THIS ARRAY. Deleting the
        // hand-written block left Project in no group, so it fell to the unclaimed fallback -- which
        // appends, and put the folder BELOW the five groups of things filed in it, beside the
        // training curriculum. The registry was right and the position was lost with the hard-coding
        // it was tangled up in. `PLACED_CODES` keeps the position without taking the registry back.
        //
        // NOT "Project Plan", WHICH IS A DIFFERENT FAMILY and reachable from its own entry. That
        // one is phases, milestones and the equipment reserved against them: an execution artefact
        // with no customer. These two share the word and nothing else.
        ...PROJECTS
            .filter(c => hasPrivilege(objectPrivilegeCode(c, "view")))
            .map(c => {
                const Icon = c.icon;
                return {key: c.code, icon: <Icon sx={iconSx}/>, label: c.plural, route: c.route,
                        subitems: []};
            }),
        // WORK STREAMS, UNDER PROJECTS AND BEFORE THE RECORDS (2026-10-28, from the source product):
        // every demand's path from request to build, one stream per quote request. Not a
        // classification -- a stream is derived from the chain, not stored -- so it is entered by
        // hand here, gated the way the folder it reads is.
        ...(PROJECTS.some(c => hasPrivilege(objectPrivilegeCode(c, "view")))
            ? [{key: "workstreams", icon: <TimelineOutlined sx={iconSx}/>, label: "Work Streams",
                route: "/work-streams", subitems: [] as any[]}]
            : []),
        // FIRST AMONG THE RECORDS, AND NOT ONE OF THEM. A change order is the thing you open to
        // change several records at once, so it sits at the top of the list it acts on. It is NOT
        // in the classification registry below because it is not an object: it has a type and a
        // form like a record does, but no revision chain -- a change order happens once, it is not
        // revised -- so it lives in its own table and gets its entry by hand here.
        ...(records ? [{key: "changeorder", icon: <PublishedWithChangesOutlined sx={iconSx}/>, label: "Change Orders",
                        route: "/change-orders", subitems: [] as SubItem[]}] : []),
        // BESIDE CHANGE ORDERS AND FOR THE SAME REASON. A document import is not an object either:
        // it has no revision chain, because an import happens once and the thing that gets revised
        // is the Document it becomes. So it is not in the classification registry below and gets
        // its entry by hand here too.
        //
        // GATED ON `document:create` rather than on sight alone: this rail entry leads to a queue
        // somebody works, and offering it to a reader who cannot stage, correct or promote
        // anything would be an invitation to a page of disabled buttons.
        ...(hasPrivilege("document:create")
            ? [{key: "documentimports", icon: <DocumentScannerOutlined sx={iconSx}/>,
                label: "Document Imports", route: "/document-imports", subitems: []}]
            : []),
        // QMS FOLDERS (Ed, 2026-11-03: "its own rail item"). Not a classification: a folder places records rather than
        // being one, so it is entered by hand here, just above the records it holds, and offered with folder:view.
        ...(hasPrivilege("folder:view")
            ? [{key: "folders", icon: <FolderOutlined sx={iconSx}/>, label: "Folders", route: "/folders", subitems: [] as any[]}]
            : []),
        // THE CLASSIFICATION REGISTRY, GROUPED BY SUBJECT. See GROUPS above for why: sixteen
        // flat entries meant sixteen 22px glyphs nobody could tell apart.
        //
        // STILL DRIVEN BY THE REGISTRY -- the groups name codes, and the label, route and icon of
        // each member come from its own row, so a renamed or rerouted classification carries both
        // into the flyout and a rail entry can no more disagree with its route than before.
        //
        // OFFERED ONLY TO SOMEBODY WITH `<type>:view`. Every capability level that grants any access
        // to a type carries it -- View, Editing and Full -- so this removes the entry only for a
        // type the caller has no capability on at all, and administrators keep everything.
        //
        // A GROUP WITH NOTHING LEFT IN IT DOES NOT APPEAR, which matters more now than it did: a
        // reader with no commerce capability at all used to lose five separate entries and now
        // loses one, and an empty flyout header would be a door onto nothing.
        //
        // RAIL TIDYING, NOT A BOUNDARY, and the distinction matters enough to write down. Reads are
        // governed by the persona's VISIBILITY clause, not by `<type>:view`: object_revisions.get
        // has no privilege guard (only edit and delete do), so typing the route still reaches a list
        // scoped to what the persona can see. This stops the rail advertising pages somebody has no
        // business on; it is not what stops them reading records, and must not be mistaken for it.
        ...groupedCategories,
        ...unclaimedCategories,
        // A FLYOUT, NOT A ROUTED ENTRY, because the two pages here answer different questions and
        // neither is the other's landing page. "What is waiting on me" is a working queue people
        // open several times a day; "who has to sign what" is configuration an administrator sets
        // and rarely revisits. Collapsing them under one route would make the rare page the front
        // door of the frequent one.
        // ONLY WHERE THERE ARE RECORDS (see keepsRecords), as Change Orders, Units of Measure and Training.
        ...(!records ? [] : [{
            key: "approvals", icon: <HowToRegOutlined sx={iconSx}/>, label: "Approvals",
            // THE QUEUE IS FOR EVERYONE, THE MATRIX IS FOR ADMINISTRATORS. Configuring who must
            // sign is administration and the server refuses it without `administrator`, so showing
            // the entry to everybody would be offering a door that always answers 403. The queue
            // below it is the opposite: being named by the matrix is what makes an approver, and
            // that has nothing to do with holding a privilege.
            //
            // UX ONLY. The gate is the server's; this just avoids advertising a refusal.
            subitems: [
                {label: "My Approvals", route: "/approvals"},
                ...(hasPrivilege("administrator")
                    ? [{label: "Approval Matrix",
                        route: "/object-approval-matrix"}]
                    : []),
            ],
        }]),
        // UNITS OF MEASURE (2026-10-30): the vocabulary every quantity in the product is stated in,
        // with the base unit per dimension and each unit's factor to it. Reference data, so gated on
        // nothing: the pick list is already in every form, and reading what a kilogram is withholds
        // nothing. Declaring a factor is administration, and the page draws that only for
        // `administrator` because the server refuses it to anybody else.
        ...(records ? [{key: "units", icon: <StraightenOutlined sx={iconSx}/>, label: "Units of Measure",
                        route: "/units-of-measure", subitems: [] as SubItem[]}] : []),
        // A FLYOUT, FOR THE SAME REASON APPROVALS IS ONE, and grouped the same way: by SUBJECT, not by
        // whose it is. "What I owe" and "who owes what" are two faces of training, and keeping them
        // together is what makes the pair findable -- the queue explains the configuration and the
        // configuration explains the queue.
        //
        // THIS IS WHY THERE IS NO "MY..." SECTION. Every such page has a counterpart beside it --
        // My Training/Training Curricula, My Approvals/Approval Matrix -- so a section gathering all
        // the "mine" pages would split every one of those pairs at once, and it would mean something
        // different in each app of the suite. Aggregation across subjects belongs on the DASHBOARD,
        // which is one surface for "what needs me" and needs no navigation at all.
        ...(!records ? [] : [{
            key: "training", icon: <SchoolOutlined sx={iconSx}/>, label: "Training",
            subitems: [
                {label: "My Training", route: "/my-training"},
                // LAST IN THE GROUP, because it is where somebody goes to ADMINISTER training
                // rather than to do it. Gated on nothing: the page is bounded by the caller's
                // organization and shows counts of what that organization already owes, so it
                // withholds nothing the record lists do not.
                {label: "Training Metrics",
                 route: "/training-metrics"},
                // SAME GATE AS EVERY OTHER CLASSIFICATION. Asked for on its own -- a curriculum is
                // configuration rather than daily work -- but applied from the registry so this is
                // not the rail's one special case. The verb is `view`, not `create`: View access
                // carries trainingCurriculum:view and not :create, and somebody who must read what
                // their people owe without authoring it is exactly who that level is for. The New
                // button stays gated on :create in ObjectList.
                ...TRAINING_CURRICULA
                    .filter(c => hasPrivilege(objectPrivilegeCode(c, "view")))
                    .map(c => ({label: c.plural, route: c.route})),
            ],
        }]),
        // LAST, AND DELIBERATELY SO. Documentation is not a place work gets done, so it sits below
        // everything that is; it is also the one entry that is always there, which makes it a stable
        // anchor at the foot of a rail whose middle is driven by the classification registry.
        {key: "documentation", icon: <HelpOutlineOutlined sx={iconSx}/>, label: "Documentation",
         route: "/documentation", subitems: [], trailing: true},
    ];

    const isActiveRoute = (route?: string) => {
        if (!route) return false;
        return location.pathname === route || location.pathname.startsWith(route + "/");
    };

    const categoryActive = (cat: Category) => {
        if (cat.route && isActiveRoute(cat.route)) return true;
        return cat.subitems.some(s => isActiveRoute(s.route));
    };

    const clearCloseTimer = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
            setOpenKey(null);
            setAnchorEl(null);
            closeTimerRef.current = null;
        }, CLOSE_DELAY_MS);
    };

    const openMenu = (key: string, el: HTMLElement) => {
        clearCloseTimer();
        setOpenKey(key);
        setAnchorEl(el);
    };

    const closeMenuNow = () => {
        clearCloseTimer();
        setOpenKey(null);
        setAnchorEl(null);
    };

    const handleCategoryClick = (cat: Category) => {
        if (!cat.route) return;
        closeMenuNow();
        navigate(cat.route);
    };

    const handleSelectSubitem = (sub: SubItem) => {
        closeMenuNow();
        navigate(sub.route);
    };

    const toggleExpanded = () => {
        closeMenuNow();
        setExpanded(v => !v);
    };

    React.useEffect(() => () => clearCloseTimer(), []);

    const openCategory = categories.find(c => c.key === openKey) ?? null;

    // No items -> no rail. Without this the sidebar still occupies 64px and offers an expander that
    // opens onto nothing.
    if (categories.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                width: expanded ? OPEN_WIDTH : CLOSED_WIDTH,
                transition: "width 200ms ease",
                backgroundColor: White,
                borderRight: `1px solid ${Grey2}`,
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
                overflowY: "auto",
                height: "100%",
            }}
        >
            {/* Expander header */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: expanded ? "flex-end" : "center",
                    padding: "8px 10px",
                    borderBottom: `1px solid ${Grey2}`,
                    minHeight: "48px",
                    boxSizing: "border-box",
                    flexShrink: 0,
                }}
            >
                <Tooltip title={expanded ? "Collapse" : "Expand"} placement="right">
                    <IconButton onClick={toggleExpanded} size="small">
                        {expanded ? <ChevronLeft/> : <ChevronRight/>}
                    </IconButton>
                </Tooltip>
            </div>

            <div style={{display: "flex", flexDirection: "column", paddingTop: "8px"}}>
                {categories.map((cat, cIdx) => {
                    const active = categoryActive(cat);
                    const hasSub = cat.subitems.length > 0;
                    const headerClickable = !!cat.route;

                    const button = (
                        <IconButton
                            className={css_buttons.blueIconButton}
                            style={{
                                height: "40px",
                                width: "40px",
                                flexShrink: 0,
                                backgroundColor: active && !expanded ? ACTIVE_BG : undefined,
                            }}
                            onClick={() => handleCategoryClick(cat)}
                        >
                            {cat.icon}
                        </IconButton>
                    );

                    return (
                        <div key={cat.key}>
                            {/* THE ONE DIVIDER IN THE RAIL, above the trailing group.
                                There used to be one before EVERY collapsed category, which marked
                                nothing: a separator that appears everywhere separates nothing, and
                                a rail of fifteen record types broken into fifteen pieces reads as a
                                list of unrelated things. Documentation -- and Settings when this
                                app grows one -- are about the app rather than destinations within
                                it, and that is the one distinction worth a line.

                                SHOWN IN BOTH STATES, unlike the old one. It marks a group rather
                                than decorating a gap, so hiding it when expanded would lose the
                                grouping exactly when there is room to show it. */}
                            {cat.trailing && !categories[cIdx - 1]?.trailing && (
                                <div style={{height: "1px", backgroundColor: Grey2, margin: "8px 12px"}}/>
                            )}

                            {/* Category header row */}
                            <div
                                onMouseEnter={(e) => {
                                    // ONLY WHEN COLLAPSED. Open, the members are already drawn
                                    // below; a flyout would cover them with a copy of themselves.
                                    if (hasSub && !expanded) {
                                        const btn = e.currentTarget.querySelector("button") as HTMLElement | null;
                                        openMenu(cat.key, btn ?? e.currentTarget);
                                    }
                                }}
                                onMouseLeave={scheduleClose}
                                onClick={expanded ? () => handleCategoryClick(cat) : undefined}
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: expanded ? "12px" : 0,
                                    padding: expanded ? "7px 16px" : "4px 10px",
                                    cursor: expanded ? (headerClickable ? "pointer" : "default") : (hasSub || headerClickable ? "pointer" : "default"),
                                    borderLeft: active && expanded && headerClickable ? `3px solid ${Blue3}` : "3px solid transparent",
                                    backgroundColor: active && expanded && headerClickable ? ACTIVE_BG : undefined,
                                }}
                            >
                                {expanded ? (
                                    <span style={{display: "inline-flex", alignItems: "center",
                                                  justifyContent: "center", width: 22, height: 22,
                                                  flexShrink: 0,
                                                  color: active && headerClickable ? Blue3 : Grey7}}>
                                        {cat.icon}
                                    </span>
                                ) : button}
                                {expanded && (
                                    <Typography style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: active && headerClickable ? Blue3 : Grey7,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}>
                                        {cat.label}
                                    </Typography>
                                )}
                            </div>

                            {/* THE SUBITEMS, UNDER THEIR HEADER, WHEN THERE IS ROOM FOR THEM.
                                Ed: "when the side menu expands, can you show the subitems under
                                each header item?"

                                A FLYOUT IS WHAT YOU DO WHEN YOU HAVE 64 PIXELS. Collapsed, a group
                                is one glyph and its members can only be reached by hovering -- so
                                the hover menu is the whole of the rail's depth. Open, the rail is
                                280px and the members fit, and making a reader hover a header to
                                learn what is in it is asking them to discover the same list twice.
                                The group names a domain; the domain's pages are what they came for.

                                AND THE FLYOUT STANDS DOWN rather than drawing the same list over
                                the top of itself -- see the header row's onMouseEnter, which now
                                opens the menu only when the rail is collapsed.

                                TEXT ALONE, WHICH IS THE DATA CATALOG'S ANSWER AND NOT MY FIRST ONE.
                                Ed: "look at the data catalog side menu as a model." t5's expanded
                                rail is a compact TEXT list -- a small icon beside a group's label,
                                its children beneath as text alone, indented to align under that
                                label -- and its own comment says why: keeping 40x40 buttons in both
                                states made the open rail "a column of tall heavy tiles, the right
                                structure rendered in the wrong idiom". This rail had exactly that,
                                plus an icon on every child, which is a second column of glyphs
                                under a rail whose whole problem was too many glyphs.
                                SO THE CHILD ICONS ARE GONE, including the grey ones asked for a
                                moment earlier: the model does not have them, and greying them was
                                a step towards quieting a column this removes. */}
                            {expanded && hasSub && (
                                <div style={{display: "flex", flexDirection: "column",
                                             paddingBottom: "2px"}}>
                                    {cat.subitems.map((sub, i) => (
                                        <SubitemRow
                                            key={i}
                                            inset={true}
                                            label={sub.label}
                                            active={isActiveRoute(sub.route)}
                                            onClick={() => handleSelectSubitem(sub)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Hover popup — works in both collapsed and expanded modes */}
            <Popper
                open={!!openCategory && !!anchorEl}
                anchorEl={anchorEl}
                placement="right-start"
                modifiers={[{name: "offset", options: {offset: [0, expanded ? 164 : 8]}}]}
                style={{zIndex: 1300}}
            >
                <Paper
                    elevation={6}
                    onMouseEnter={clearCloseTimer}
                    onMouseLeave={scheduleClose}
                    sx={{
                        minWidth: 260,
                        borderRadius: "10px",
                        border: `1px solid ${Grey2}`,
                        overflow: "hidden",
                    }}
                >
                    <div style={{padding: "10px 14px 8px", borderBottom: `1px solid ${Grey2}`, backgroundColor: HOVER_BG}}>
                        <Typography sx={{fontSize: 12, fontWeight: 700, color: Grey5, textTransform: "uppercase", letterSpacing: "0.5px"}}>
                            {openCategory?.label}
                        </Typography>
                    </div>
                    <div style={{display: "flex", flexDirection: "column", padding: "4px 0"}}>
                        {openCategory?.subitems.map((sub, i) => {
                            const subActive = isActiveRoute(sub.route);
                            return (
                                <SubitemRow
                                    key={i}
                                    label={sub.label}
                                    active={subActive}
                                    onClick={() => handleSelectSubitem(sub)}
                                />
                            );
                        })}
                    </div>
                </Paper>
            </Popper>
        </div>
    );
}

/**
 * One member of a group: in the hover flyout, and -- with `inset` -- nested under its header in
 * the open rail.
 *
 * ONE COMPONENT FOR BOTH SO THEY CANNOT DISAGREE about which page is the active one. The two
 * places differ only in their padding and type size; everything that decides what the row SAYS is
 * shared. Neither draws an icon: the flyout is a short list under a heading that already names the
 * domain, and the inset rows follow the data catalog's rail, which indents its children under the
 * group's LABEL and lets the indent do the work.
 */
function SubitemRow({inset, label, active, onClick}:
                    {inset?: boolean; label: string; active: boolean; onClick: () => void}) {
    const [hover, setHover] = React.useState(false);
    const bg = active ? ACTIVE_BG : (hover ? HOVER_BG : "transparent");
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: "flex",
                alignItems: "center",
                // 48px, WHICH IS THE HEADER'S LABEL AND IS ARITHMETIC RATHER THAN TASTE:
                // 16px padding + 22px icon + 12px gap, less the 3px accent border the row carries
                // itself. The data catalog's ChildRow computes it exactly this way and its header
                // row has the same three numbers, so the children line up under the label instead
                // of under the glyph. Ed: "indent further" / "move the subitems to the right a
                // little more" -- both were nudges at a rail whose header was still a 40px button;
                // once the header became a 22px icon at 16px, the alignment is a consequence.
                // 50, WHICH IS EXACT. A child's text starts at border + padding = 3 + 50 = 53,
                // and the header's label starts at 3 + 16 + 22 + 12 = 53: the same pixel. The
                // model's 48 left two pixels of drift under the very thing the row hangs from,
                // which is near enough to aligned that the eye expects alignment and far enough
                // off to notice. Ed, a pixel at a time: "move the text for the subitems to the
                // right by 1px", then "it appears the subitems can move 1px to the right to
                // vertically align perfectly." They can, and this is the arithmetic that says so.
                padding: inset ? "5px 16px 5px 50px" : "8px 16px",
                cursor: "pointer",
                backgroundColor: bg,
                borderLeft: active ? `3px solid ${Blue3}` : "3px solid transparent",
            }}
        >
            <Typography sx={{fontSize: inset ? 13.5 : 14,
                             fontWeight: active ? 600 : (inset ? 400 : 500),
                             color: active ? Blue3 : (inset ? Grey7 : Grey17),
                             whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                {label}
            </Typography>
        </div>
    );
}
